import App from '@/App';
import { NavigateWithUrlSearchParams } from '@/components/NavigateWithUrlSearchParams';
import Account from '@/routes/account';
import Tickets from '@/routes/account/tickets/Tickets';
import TravelPasses from '@/routes/account/travelPasses/TravelPasses';
import Login from '@/routes/login';
import { BookingDetails } from '@/routes/manage/BookingDetails';
import BookingSearch from '@/routes/manage/BookingSearch';
import EditPassengerDetails from '@/routes/manage/EditPassengerDetails';
import { ManageBookingLayout } from '@/routes/manage/ManageBookingLayout';
import Purchase from '@/routes/purchase';
import Checkout from '@/routes/purchase/Checkout';
import InboundJourneyList from '@/routes/purchase/InboundJourneyList';
import InboundJourneyOfferList from '@/routes/purchase/InboundJourneyOfferList';
import NonTripOfferList from '@/routes/purchase/NonTripOfferList';
import OutboundJourneyList from '@/routes/purchase/OutboundJourneyList';
import OutboundJourneyOfferList from '@/routes/purchase/OutboundJourneyOfferList';
import NewPasswordFormWithGuard from '@/routes/reset-password/NewPasswordFormWithGuard';
import PasswordResetLinkForm from '@/routes/reset-password/PasswordResetLinkForm';
import SignUp from '@/routes/signup';
import SignupConfirmation from '@/routes/signup/SignupConfirmation';
import Success from '@/routes/success/Success';
import { Version } from '@/routes/version';
import { store } from '@/store';
import { assertEnvVars } from '@/utils/assertEnvVars';
import { silenceDomMutationErrors } from '@/utils/dom';
import { initializeGTM, setDefaultConsent } from '@/utils/gtm';
import { isLocalDevelopment } from '@/utils/isLocalDev';
import { initializeSentry } from '@/utils/sentry';
import { getInitialTenant, getTenantConfiguration } from '@/utils/tenant';
import * as Sentry from '@sentry/react';
import { Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
} from 'react-router-dom';

import '@/i18n';
import AccountDetails from '@/routes/account/accountDetails/AccountDetails';
import AccountSettings from '@/routes/account/accountDetails/AccountSettings';
import Loyalty from '@/routes/account/accountDetails/Loyalty';
import MyInformation from '@/routes/account/accountDetails/MyInformation';
import ChangePassword from '@/routes/account/changePassword/ChangePassword';
import TicketsList from '@/routes/account/tickets/TicketsList';
import TravelPassesList from '@/routes/account/travelPasses/TravelPassesList';
import Vouchers from '@/routes/account/Vouchers';
import VouchersList from '@/routes/account/vouchers/VouchersList';
import '@/styles/index.css';
import '@/styles/toastify.css';
import '@turnit-ride-ui/webshop-search-widget/style.css';
import 'react-toastify/dist/ReactToastify.css';

initializeSentry();
assertEnvVars();
silenceDomMutationErrors();

const initializeGoogleTagManager = () => {
  const initialTenant = getInitialTenant();
  const tenantConfig = getTenantConfiguration(initialTenant.value);
  const gtmContainerId = import.meta.env.VITE_APP_GTM_CONTAINER_ID;

  if (
    tenantConfig?.enableGoogleTagManager &&
    gtmContainerId &&
    gtmContainerId !== 0
  ) {
    // Set the safe consent default BEFORE GTM loads. Cookiebot then sends
    // consent updates once the user makes a choice. Only for tenants whose
    // Cookiebot is wired for Consent Mode v2 (see enableConsentMode).
    if (tenantConfig.enableConsentMode) {
      setDefaultConsent();
    }
    initializeGTM(gtmContainerId);
  }
};

initializeGoogleTagManager();

const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouter(createBrowserRouter);

const router = sentryCreateBrowserRouter([
  {
    path: '/',
    element: <App />,
    children: [
      { index: true, element: <NavigateWithUrlSearchParams to="/purchase" /> },
      {
        path: 'purchase',
        element: <Purchase />,
        children: [
          { path: 'outbound/journey', element: <OutboundJourneyList /> },
          { path: 'outbound/offer', element: <OutboundJourneyOfferList /> },
          { path: 'inbound/journey', element: <InboundJourneyList /> },
          { path: 'inbound/offer', element: <InboundJourneyOfferList /> },
          { path: 'non-trip/offer', element: <NonTripOfferList /> },
          { path: 'checkout', element: <Checkout /> },
          { path: '*', element: <Navigate to="/purchase" replace /> },
        ],
      },
      {
        path: 'success',
        element: <Success />,
      },
      {
        path: 'manage-booking',
        element: <ManageBookingLayout />,
        children: [
          {
            index: true,
            element: <BookingSearch />,
          },
          { path: 'details', element: <BookingDetails /> },
          {
            path: 'edit-passengers',
            element: <EditPassengerDetails />,
          },
          {
            path: '*',
            element: <Navigate to="/manage-booking" replace />,
          },
        ],
      },
      ...(isLocalDevelopment()
        ? [
            {
              path: 'login',
              children: [
                { index: true, element: <Login /> },
                { path: '*', element: <Navigate to="/login" replace /> },
              ],
            },
            {
              path: 'reset-password',
              children: [
                { index: true, element: <PasswordResetLinkForm /> },
                {
                  path: ':passwordResetHash',
                  element: <NewPasswordFormWithGuard />,
                },
                {
                  path: '*',
                  element: <Navigate to="/reset-password" replace />,
                },
              ],
            },

            {
              path: 'signup',
              children: [
                { index: true, element: <SignUp /> },
                { path: 'confirmation', element: <SignupConfirmation /> },
                { path: '*', element: <Navigate to="/signup" replace /> },
              ],
            },
            {
              path: 'account',
              element: <Account />,
              children: [
                {
                  index: true,
                  element: <Navigate to="/account/details" replace />,
                },
                {
                  path: 'tickets',
                  element: <Tickets />,
                  children: [
                    {
                      index: true,
                      element: (
                        <Navigate to="/account/tickets/upcoming" replace />
                      ),
                    },
                    {
                      path: 'upcoming',
                      element: <TicketsList timeType="active" />,
                    },
                    {
                      path: 'history',
                      element: <TicketsList timeType="history" />,
                    },
                  ],
                },
                {
                  path: 'travel-passes',
                  element: <TravelPasses />,
                  children: [
                    {
                      index: true,
                      element: (
                        <Navigate to="/account/travel-passes/active" replace />
                      ),
                    },
                    {
                      path: 'active',
                      element: <TravelPassesList timeType="active" />,
                    },
                    {
                      path: 'history',
                      element: <TravelPassesList timeType="history" />,
                    },
                  ],
                },
                {
                  path: 'vouchers',
                  element: <Vouchers />,
                  children: [
                    {
                      index: true,
                      element: (
                        <Navigate to="/account/vouchers/active" replace />
                      ),
                    },
                    {
                      path: 'active',
                      element: <VouchersList timeType="active" />,
                    },
                    {
                      path: 'history',
                      element: <VouchersList timeType="history" />,
                    },
                  ],
                },
                {
                  path: 'details',
                  element: <AccountDetails />,
                  children: [
                    {
                      index: true,
                      element: <Navigate to="/account/details/info" replace />,
                    },
                    {
                      path: 'info',
                      element: <MyInformation />,
                    },
                    {
                      path: 'settings',
                      element: <AccountSettings />,
                    },
                    {
                      path: 'loyalty',
                      element: <Loyalty />,
                    },
                  ],
                },
                {
                  path: 'change-password',
                  element: <ChangePassword />,
                },
                {
                  path: '*',
                  element: <Navigate to="/account/details" replace />,
                },
              ],
            },
          ]
        : []),
      {
        path: 'version',
        element: <Version />,
      },
      { path: '*', element: <NavigateWithUrlSearchParams to="/purchase" /> },
    ],
  },
]);

ReactDOM.createRoot(document.getElementById('root')!).render(
  <Suspense fallback={<span />}>
    <Provider store={store}>
      <RouterProvider router={router} />
    </Provider>
  </Suspense>
);
